/**
 * Database fetch functions for persona profiles.
 * These query the Supabase tables and surface failures to the caller.
 */

import { supabase } from '@/integrations/supabase/client';
import type { PersonaRow } from '@/lib/types/catalog';

/**
 * Fetch persona profiles from the database.
 * Throws when the query fails or returns no rows so callers can handle the failure.
 */
export async function fetchPersonaRows(): Promise<PersonaRow[]> {
  try {
    console.info('[Personas] fetchPersonaRows start');
    const supabaseClient = supabase as any;
    const { data, error } = await supabaseClient
      .from('personas')
      .select('*')
      .order('mix_percent', { ascending: false });

    if (error) {
      console.error('Failed to fetch persona profiles from Supabase:', error.message);
      throw new Error(`Failed to fetch persona profiles: ${error.message}`);
    }

    if (!data || data.length === 0) {
      console.error('Supabase returned no persona profiles.');
      throw new Error('No persona profiles available from Supabase.');
    }

    console.info('[Personas] fetchPersonaRows success', {
      count: data.length,
      ids: data.map((p) => (p as PersonaRow).id).filter(Boolean),
    });

    return Array.isArray(data) ? (data as unknown as PersonaRow[]) : [];
  } catch (err) {
    if (err instanceof Error) {
      console.error('[Personas] fetchPersonaRows error', err);
      throw err;
    }
    console.error('[Personas] fetchPersonaRows unknown error', err);
    throw new Error('Unknown error fetching persona profiles.');
  }
}
